import styled, { css } from 'styled-components'

const GRAY = '#7D7D7D'
const LINE = '#EEEDED'
const BLACK = '#111111'
const BLUE = '#076AFF'
const LINK = '#0074FE'

// Each row is its own grid, so every column needs the same width in all of
// them: the actions get a fixed one and the text columns give way around it.
export const ROW_COLUMNS =
  'minmax(0, 1.3fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 1fr) 290px'

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  width: 100%;
  background: #fff;
  padding: 24px 24px 0;
  overflow: hidden;
  box-sizing: border-box;
`

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  flex-shrink: 0;

  h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    color: ${BLACK};
  }

  p {
    margin: 6px 0 0;
    font-size: 13px;
    color: ${GRAY};
  }
`

export const Count = styled.div`
  font-size: 13px;
  color: ${GRAY};

  b {
    color: ${BLACK};
    font-size: 15px;
    font-weight: 500;
  }
`

export const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  flex-shrink: 0;
`

export const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  width: 220px;
  font-size: 14px;
  background: #fff;
  color: ${BLACK};

  &::placeholder {
    color: #999;
  }
`

export const FilterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 10px 6px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;

  b {
    color: ${BLACK};
    font-weight: 500;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

export const CustomRange = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 6px;
  padding: 10px 16px 6px;
  border-top: 1px solid ${LINE};

  b {
    font-size: 13px;
    font-weight: 500;
  }

  label {
    display: grid;
    gap: 3px;
    font-size: 12px;
    color: ${GRAY};
  }

  input {
    height: 32px;
    padding: 0 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
    color: ${BLACK};
  }
`

export const Table = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-bottom: 24px;
`

const rowGrid = css`
  display: grid;
  grid-template-columns: ${ROW_COLUMNS};
  gap: 14px;
  align-items: center;
  padding: 12px 16px;

  > * {
    min-width: 0;
  }
`

export const HeaderRow = styled.div`
  ${rowGrid};
  padding-top: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  border-bottom: 1px solid #f0f0f0;
`

export const DayRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
  padding: 18px 16px 8px;
  border-bottom: 1px solid ${LINE};
  font-size: 13px;
  font-weight: 500;
  color: ${BLACK};

  span {
    color: ${GRAY};
    font-weight: 400;
  }
`

export const Row = styled.div`
  ${rowGrid};
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #333;
  transition: background 0.3s;
  background: ${({ $confirmed }) => ($confirmed ? '#eef7e1' : 'transparent')};

  &:hover {
    background: ${({ $confirmed }) => ($confirmed ? '#eef7e1' : '#fafafa')};
  }
`

export const Sub = styled.span`
  display: block;
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
`

export const PatientName = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: ${BLACK};
  text-align: left;
  cursor: pointer;

  &:hover {
    color: ${LINK};
  }
`

export const VirtualTag = styled.span`
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 3px;
  background: #e8dff9;
  color: #4b2a8f;
  font-size: 10px;
  text-transform: uppercase;
  vertical-align: 1px;
`

export const Phone = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  white-space: nowrap;
  color: ${BLACK};
  text-decoration: none;
  font-variant-numeric: tabular-nums;

  svg {
    flex: none;
    width: 16px;
    height: 16px;
    color: ${GRAY};
  }

  &:hover,
  &:hover svg {
    color: ${LINK};
  }
`

const TONES = {
  done: '#05AD48',
  waiting: '#F9D96B',
  missing: '#FC7777',
}

export const HealthHistory = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: none;
    background: ${({ $tone }) => TONES[$tone]};
  }
`

export const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
`

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 2px 12px 0;
  border: 1px solid ${LINE};
  border-radius: 4px;
  background: #fff;
  color: ${BLACK};
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${LINE};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  ${({ $variant }) =>
    $variant === 'confirm' &&
    css`
      border-color: ${BLUE};
      background: ${BLUE};
      color: #fff;

      &:hover {
        background: #0057d9;
      }
    `}

  ${({ $variant }) =>
    $variant === 'cancel' &&
    css`
      color: #d32f2f;

      &:hover {
        background: #fdecea;
      }
    `}
`

export const Confirmed = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #05ad48;
  font-size: 13px;
  font-weight: 500;

  button {
    padding: 0;
    border: 0;
    background: none;
    color: ${LINK};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
  }
`

export const Empty = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: ${GRAY};
  font-size: 14px;

  b {
    display: block;
    margin-bottom: 4px;
    color: ${BLACK};
    font-size: 16px;
    font-weight: 500;
  }
`

export const Muted = styled.span`
  color: #999;
`
